import { type TFunction } from "i18next"

import { type Event } from "../../types/types.ts"
import { appointmentTypeLabelKey } from "../constants/appointment-types.ts"

type CalendarEventRecord = Pick<
  Event,
  "lead" | "project" | "contact" | "category"
> & {
  // The meeting's own address (arriving with the meeting-location work);
  // until every stored event carries it, the lead's contact address stands in.
  address?: string | null
}

interface CalendarEventCardInput {
  title?: string | null
  record?: CalendarEventRecord
}

// Null, not a placeholder: an event stored before the type picker has no
// type to name.
const calendarEventTypeLabel = (
  { record }: CalendarEventCardInput,
  t: TFunction
): string | null => {
  const category = record?.category
  if (!category) return null

  return t(appointmentTypeLabelKey(category))
}

// The one place both "is this event tied to a customer" and "what do we call
// them" get decided — `calendarEventCardRecordParts` needs the same value, so
// it reads through this rather than re-deriving it from `record.contact`.
const calendarEventCardCustomerName = ({
  record
}: CalendarEventCardInput): string | null => record?.contact?.customer_name?.trim() || null

// A title that says nothing beyond its own type ("Proposal" on a Proposal,
// "Meeting" on a Meeting — case/whitespace aside) carries no information a
// reader doesn't already have from the type badge itself, so it's treated
// as absent rather than shown as a stutter ("Proposal: Proposal"). Exact
// match only, on purpose — a title that merely contains the type word
// ("Proposal Review") is left alone rather than guessed at with fuzzy
// matching.
const isGenericTitle = (title: string, typeLabel: string | null): boolean =>
  !!typeLabel && title.trim().toLowerCase() === typeLabel.trim().toLowerCase()

// The one place that decides what a calendar card's primary line actually
// identifies — every surface that renders a card reads through this rather
// than reimplementing the fallback chain, so there is exactly one rule
// instead of a slightly different one per view. In order: the linked
// customer (who a meeting is actually with outranks everything else); the
// organizer's own title, but only when it says something the type badge
// doesn't already ("Review final proposal with homeowner", not a bare
// "Proposal"); the linked lead; the linked project; then nothing, which
// leaves the bare type as the last resort.
//
// Lead is checked before project deliberately — this is the opposite order
// from `calendarEventCardRecordParts`' *secondary* record line (which
// prefers project when both exist), because this is the identity a reader
// scans for first, and the client's own examples pin lead ahead of project
// here specifically.
const calendarEventCardContextName = (
  input: CalendarEventCardInput,
  t: TFunction
): string | null => {
  const customerName = calendarEventCardCustomerName(input)
  if (customerName) return customerName

  const typeLabel = calendarEventTypeLabel(input, t)
  const title = input.title?.trim()
  if (title && !isGenericTitle(title, typeLabel)) return title

  const leadName = input.record?.lead?.name?.trim()
  if (leadName) return leadName

  const projectName = input.record?.project?.name?.trim()
  if (projectName) return projectName

  return null
}

// A lead/customer meeting is identified by WHO it's with, not by whatever the
// organizer typed as a title — the customer's own escalation (all short
// events "look the same") is that free-typed titles ("Kitchen walkthrough",
// "Meeting") stop reading as distinct once a card is a few pixels tall. The
// same escalation, generalized: an event with nothing customer-specific
// still deserves better than a bare "Proposal" when a title, lead, or
// project name could say what it's actually about — see
// `calendarEventCardContextName` for the full fallback chain. Whichever tier
// resolves, the type still vacates its own slot, same rule as standing in
// for a missing title, since it is now baked into the headline.
const calendarEventCardText = (
  input: CalendarEventCardInput,
  t: TFunction
): { title: string; typeLabel: string | null } => {
  const typeLabel = calendarEventTypeLabel(input, t)
  const contextName = calendarEventCardContextName(input, t)

  if (contextName && typeLabel) {
    return { title: `${typeLabel}: ${contextName}`, typeLabel: null }
  }
  // A legacy event stored before the type picker existed has no type to
  // prefix, so a resolved context name (most often its own stored title —
  // the only tier reachable with nothing to compare it against) stands
  // alone rather than pairing with a `null` label.
  if (contextName) {
    return { title: contextName, typeLabel: null }
  }

  // Nothing resolved at all — no customer, no usable title (empty, or a
  // stutter of the type itself, e.g. a "Proposal" title on a Proposal), no
  // lead, no project. The bare type is the last resort; it never pairs with
  // an identical `title` field, which is the double-print the "vacates its
  // slot" rule exists to prevent.
  return { title: typeLabel || "-", typeLabel: null }
}

// The record name and the person's name are two distinct lines on the card
// (spec: TIME / TYPE / LEAD·PROJECT / PERSON), never joined into one string —
// that was the previous design and read as too dense.
const calendarEventCardRecordParts = (
  { record }: CalendarEventCardInput,
  t: TFunction
): { recordName: string | null; personName: string | null } => {
  const source = record?.project || record?.lead
  if (!source) return { recordName: null, personName: null }

  // The record's own name ("Cole Backyard Renovation") is what someone
  // scanning the grid actually recognizes — its uid is an internal
  // identifier with no meaning at a glance, so it never appears here.
  const kind = record?.project ? "project" : "lead"
  const recordName = source.name?.trim() || t(`calendarEvent.record.${kind}`)
  const personName = calendarEventCardCustomerName({ record })
  return { recordName, personName }
}

// The card's second row. A follow-up is a call, not a visit — it stays a
// one-line card with no address by design.
const calendarEventCardAddress = ({
  record
}: CalendarEventCardInput): string | null => {
  if (record?.category === "FOLLOW_UP") return null

  const own = record?.address?.trim()
  if (own) return own

  const contact = record?.contact
  const fallback = [contact?.address?.trim(), contact?.city?.trim()]
    .filter(Boolean)
    .join(", ")
  return fallback || null
}

export {
  calendarEventCardAddress,
  calendarEventCardContextName,
  calendarEventCardCustomerName,
  calendarEventCardRecordParts,
  calendarEventCardText
}
